import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {TreatmentRequestAuthorizationValidationSchema} from '@/features/treatment-requests/types'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {isInCurrentCalendarYear, ObjectKeys} from '@/utilities/helpers'
import {FormFieldBox} from '@/components/ui/form-atoms/FormAtoms'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {authorizationToLabel} from '@/features/treatment-requests/utils'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputDate} from '@/components/commons/input-date-legacy/InputDate'
import dayjs from 'dayjs'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {useEffect} from 'react'
import {useGetPatient} from '@/features/patients/services/useGetPatient'

export const TreatmentRequestAuthorizationForm = ({patientId}: {patientId: string}) => {
    const {t} = useTranslation()

    // methods
    const form = useFormContext<TreatmentRequestAuthorizationValidationSchema>()

    // form observer
    const authorization = useWatch({control: form.control, name: 'authorization'})
    const authorizationToRequest = useWatch({control: form.control, name: 'authorizationToRequest'})

    // query
    const patientQuery = useGetPatient({
        patientId: patientId ?? undefined,
        responseType: 'extended',
        options: {
            enabled: false
        }
    })

    // side effects
    const onChangeAuthorizationSideEffects = () => {
        form.setValue('authorizationDate', null, {shouldDirty: true})
        form.setValue('authorizationToRequest', false, {shouldDirty: true})
        form.clearErrors('authorization')
    }

    useEffect(() => {
        if (
            patientQuery.isSuccess &&
            patientQuery.data?.treatmentRequest?.authorization == 'hospital_doctor' &&
            patientQuery.data?.treatmentRequest?.status == 'accepted' &&
            authorization == 'hospital_doctor' &&
            isInCurrentCalendarYear(patientQuery.data?.treatmentRequest?.authorizationDate)
        ) {
            form.setError('authorization', {
                type: 'custom',
                message: t('treatment_requests:authorization_error')
            })
        }
    }, [patientQuery?.isSuccess, patientQuery.data?.treatmentRequest, authorization])

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <Flexbox direction="column" gap={1.5} fullWidth>
                <Label>{t('commons:authorization')}</Label>
                <Controller
                    control={form.control}
                    name="authorization"
                    render={({field, fieldState}) => (
                        <Flexbox fullWidth direction="column" gap={1.5}>
                            <Grid columns={3} columnGap={4} fullWidth>
                                {ObjectKeys(authorizationToLabel).map(authorizationKey => (
                                    <FormFieldBox
                                        key={authorizationKey}
                                        spanColumn={1}
                                        hasError={!!fieldState.error?.message && fieldState.error.type != 'custom'}
                                        isSelected={field.value == authorizationKey}
                                    >
                                        <Flexbox direction="column" gap={1}>
                                            <RadioField
                                                label={t(authorizationToLabel[authorizationKey])}
                                                checked={field.value === authorizationKey}
                                                name={field.name}
                                                value={authorizationKey}
                                                onChange={value => {
                                                    field.onChange(value)
                                                    onChangeAuthorizationSideEffects()
                                                }}
                                            />
                                        </Flexbox>
                                    </FormFieldBox>
                                ))}
                                <FormFieldBox
                                    spanColumn={1}
                                    hasError={
                                        !!form.formState.errors.authorization?.message &&
                                        form.formState.errors.authorization.type != 'custom'
                                    }
                                    isSelected={field.value === null}
                                >
                                    <Flexbox direction="column" gap={1}>
                                        <RadioField
                                            label={t('commons:none_female')}
                                            checked={field.value === null}
                                            name={field.name}
                                            onChange={() => {
                                                field.onChange(null)
                                                onChangeAuthorizationSideEffects()
                                            }}
                                        />
                                    </Flexbox>
                                </FormFieldBox>
                            </Grid>
                            {fieldState.error?.message && <InputHelpText error={fieldState.error.message} />}
                        </Flexbox>
                    )}
                />
            </Flexbox>
            {authorization && (
                <Grid columns={3}>
                    <GridItem spanColumn={1} gap={4} direction="column">
                        <Controller
                            name="authorizationDate"
                            control={form.control}
                            render={({field, fieldState}) => (
                                <InputDate
                                    label={t('commons:authorization_date')}
                                    placeholder="GG/MM/AAAA"
                                    errorMessage={fieldState.error?.message}
                                    maxDate={dayjs().toDate()}
                                    numMonths={1}
                                    selectedDates={field.value ? [field.value] : []}
                                    onChange={field.onChange}
                                    disabled={
                                        form.formState.errors.authorization?.type == 'custom' || authorizationToRequest
                                    }
                                />
                            )}
                        />
                        <Checkbox
                            id="authorizationToRequest"
                            label={t('treatment_requests:to_request')}
                            disabled={form.formState.errors.authorization?.type == 'custom'}
                            checked={authorizationToRequest}
                            {...form.register('authorizationToRequest', {
                                onChange: () => {
                                    form.setValue('authorizationDate', null, {shouldDirty: true})
                                    form.clearErrors('authorizationDate')
                                }
                            })}
                        />
                    </GridItem>
                </Grid>
            )}
        </Flexbox>
    )
}
